import Player from "@vimeo/player";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { useAppDispatch } from "store/hooks";
import { setIsSoundMuted } from "store/slices/user";
import { claimPlayback } from "utils/exclusivePlayback";
import { getFirebaseAssetURL } from "utils/getFirebaseAssetURL";

interface Props {
  videoId: string;
  previewImage?: string | null | undefined;
}

const Video: FC<Props> = ({ videoId, previewImage }) => {
  const videoSrc = `https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`;
  const defaultImagePath = "/images/video-placeholder.png";

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const [placeholderImageUrl, setPlaceholderImageUrl] = useState<string | null>(previewImage ? null : defaultImagePath);

  useEffect(() => {
    if (!previewImage) {
      setPlaceholderImageUrl(defaultImagePath);
      return;
    }

    getFirebaseAssetURL(previewImage)
      .then(url => {
        setPlaceholderImageUrl(url ?? defaultImagePath);
      })
      .catch(() => {
        setPlaceholderImageUrl(defaultImagePath);
      });
  }, [previewImage]);

  useEffect(() => {
    if (!iframeRef.current) return;

    const player = new Player(iframeRef.current);
    playerRef.current = player;

    // One per player instance — `claimPlayback` needs the same identity for as long as the iframe lives.
    const stop = () => {
      player.pause().catch(() => {});
    };

    player
      .ready()
      .then(async () => {
        await player.play();
        await player.setVolume(1);
        await player.setMuted(false);
      })
      .catch(() => {});

    player.on("play", () => {
      claimPlayback(stop);
      toggleBgSound("off");
    });
    player.on("pause", () => {
      toggleBgSound("on");
    });
    player.on("ended", () => {
      toggleBgSound("on");
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        } else if ((document as any).webkitFullscreenElement) {
          (document as any).webkitExitFullscreen?.();
        }
      } catch (err) {
        // ignore
      }
    });

    return () => {
      player.off("play");
      player.off("pause");
      player.off("ended");

      toggleBgSound("on");

      (async () => {
        try {
          await player.destroy();
        } catch (err) {
          console.warn("Failed to destroy Vimeo player", err);
        }
      })();
      playerRef.current = null;
    };
  }, [isVideoLoaded]);

  const toggleBgSound = (status: "on" | "off") => {
    dispatch(setIsSoundMuted(status === "off"));
  };

  return (
    <div className="video-wrapper">
      {isVideoLoaded ? (
        <iframe ref={iframeRef} src={videoSrc} allow="autoplay; fullscreen; picture-in-picture" />
      ) : (
        <button
          onClick={() => {
            setIsVideoLoaded(true);
          }}
          style={{ backgroundImage: placeholderImageUrl ? `url("${placeholderImageUrl}")` : undefined }}
        />
      )}
    </div>
  );
};

export default Video;
